import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import ImagePicker from "./ImagePicker";

// Comprehensive Lesson Form Component
const LessonForm = ({ lesson, onSave, onCancel, isEdit = false }) => {
  const [formData, setFormData] = useState({
    lesson_number: 0,
    name: "",
    subject: "",
    lesson_maxim: "",
    intro_line: "",
    summary: "",
    science: "",
    task: "",
    image: "",
    gradient_top_color: "#1E0D2B",
    gradient_bottom_color: "#5E4242",
  });
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState("content");

  useEffect(() => {
    if (lesson && isEdit) {
      setFormData({
        lesson_number: lesson.lesson_number || 0,
        name: lesson.name || "",
        subject: lesson.subject || "",
        lesson_maxim: lesson.lesson_maxim || "",
        intro_line: lesson.intro_line || "",
        summary: lesson.summary || "",
        science: lesson.science || "",
        task: lesson.task || "",
        image: lesson.image || "",
        gradient_top_color: lesson.gradient_top_color || "#1E0D2B",
        gradient_bottom_color: lesson.gradient_bottom_color || "#5E4242",
      });
      setSuggestedQuestions(lesson.suggested_questions || []);
    }
  }, [lesson, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lessonData = {
      ...formData,
      // Convert empty image string to null
      image: formData.image.trim() === "" ? null : formData.image,
      suggested_questions: suggestedQuestions,
    };
    await onSave(lessonData);
  };

  const addPrompt = () => {
    setSuggestedQuestions([...suggestedQuestions, ""]);
  };

  const updatePrompt = (index, value) => {
    const updated = [...suggestedQuestions];
    updated[index] = value;
    setSuggestedQuestions(updated);
  };

  const removePrompt = (index) => {
    setSuggestedQuestions(suggestedQuestions.filter((_, i) => i !== index));
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <Form onSubmit={handleSubmit}>
        {/* Tab Navigation */}
        <div className="mb-4">
          <div
            style={{
              display: "flex",
              borderBottom: "2px solid #f0f0f0",
              gap: "0",
            }}
          >
            {[
              { key: "content", label: "Content", icon: faEdit },
              { key: "design", label: "Design", icon: faPlus },
              { key: "ai", label: "AI Prompts", icon: faPlus },
            ].map((tab) => (
              <Button
                key={tab.key}
                variant="link"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "12px 24px",
                  borderRadius: "0",
                  borderBottom:
                    activeTab === tab.key
                      ? "3px solid #F0D6B5"
                      : "3px solid transparent",
                  color: activeTab === tab.key ? "#635956" : "#6c757d",
                  fontWeight: activeTab === tab.key ? "600" : "400",
                  textDecoration: "none",
                  backgroundColor: "transparent",
                }}
              >
                <FontAwesomeIcon icon={tab.icon} className="me-2" />
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Content Tab */}
        {activeTab === "content" && (
          <>
            {/* Basic Info */}
            <Row className="mb-4">
              <Col md={8}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                    Chapter
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    placeholder="e.g., Foundation"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                    Lesson Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.lesson_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        lesson_number: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="e.g., 1"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                Lesson Name
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Welcome to Night School"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                Intro Line
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.intro_line}
                onChange={(e) =>
                  setFormData({ ...formData, intro_line: e.target.value })
                }
                placeholder="Brief introduction to the lesson..."
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                Summary
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.summary}
                onChange={(e) =>
                  setFormData({ ...formData, summary: e.target.value })
                }
                placeholder="Main lesson content..."
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                The Science
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.science}
                onChange={(e) =>
                  setFormData({ ...formData, science: e.target.value })
                }
                placeholder="Scientific explanation and research..."
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                Today's Task
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.task}
                onChange={(e) =>
                  setFormData({ ...formData, task: e.target.value })
                }
                placeholder="Actionable task for the user..."
              />
            </Form.Group>
          </>
        )}

        {/* Design Tab */}
        {activeTab === "design" && (
          <>
            <div className="mb-4">
              <h5 style={{ color: "#635956", marginBottom: "20px" }}>
                Lesson Design Settings
              </h5>

              {/* Background Gradient Section */}
              <div className="mb-4">
                <h6
                  style={{
                    fontWeight: "600",
                    color: "#495057",
                    marginBottom: "16px",
                  }}
                >
                  Background Gradient
                </h6>

                <Row>
                  {/* Top Color */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        style={{
                          fontWeight: "500",
                          color: "#6c757d",
                          fontSize: "14px",
                        }}
                      >
                        Top Color
                      </Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        {/* Color Preview Square */}
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "6px",
                            border: "2px solid #e9ecef",
                            background:
                              formData.gradient_top_color || "#1E0D2B",
                            flexShrink: 0,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                          title={`Preview: ${
                            formData.gradient_top_color || "#1E0D2B"
                          }`}
                        />
                        <Form.Control
                          type="text"
                          value={formData.gradient_top_color}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gradient_top_color: e.target.value,
                            })
                          }
                          placeholder="e.g., #1E0D2B"
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Bottom Color */}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        style={{
                          fontWeight: "500",
                          color: "#6c757d",
                          fontSize: "14px",
                        }}
                      >
                        Bottom Color
                      </Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        {/* Color Preview Square */}
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "6px",
                            border: "2px solid #e9ecef",
                            background:
                              formData.gradient_bottom_color || "#5E4242",
                            flexShrink: 0,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                          title={`Preview: ${
                            formData.gradient_bottom_color || "#5E4242"
                          }`}
                        />
                        <Form.Control
                          type="text"
                          value={formData.gradient_bottom_color}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gradient_bottom_color: e.target.value,
                            })
                          }
                          placeholder="e.g., #5E4242"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Image Picker */}
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                  Lesson Image
                </Form.Label>
                <ImagePicker
                  selectedImageUrl={formData.image}
                  onImageSelect={(url) =>
                    setFormData({ ...formData, image: url })
                  }
                />
                <Form.Text className="text-muted">
                  Select an image from the Night Lesson Images collection
                </Form.Text>
              </Form.Group>
            </div>
          </>
        )}

        {/* AI Prompts Tab */}
        {activeTab === "ai" && (
          <>
            {/* AI Lesson Summary */}
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: "600", color: "#495057" }}>
                AI Lesson Summary
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.lesson_maxim}
                onChange={(e) =>
                  setFormData({ ...formData, lesson_maxim: e.target.value })
                }
                placeholder="Brief AI-generated summary of the lesson for the chat bot..."
              />
              <Form.Text className="text-muted">
                This summary will be displayed prominently in the lesson view
              </Form.Text>
            </Form.Group>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 style={{ color: "#635956", marginBottom: "0" }}>
                  AI Conversation Prompts
                </h5>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={addPrompt}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Prompt
                </Button>
              </div>

              {suggestedQuestions.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <p style={{ color: "#6c757d", marginBottom: "0" }}>
                    No AI prompts yet. Click "Add Prompt" to create conversation
                    starters.
                  </p>
                </div>
              ) : (
                suggestedQuestions.map((question, index) => (
                  <div key={index} className="d-flex mb-3">
                    <Form.Control
                      type="text"
                      value={question}
                      onChange={(e) => updatePrompt(index, e.target.value)}
                      placeholder="Enter AI conversation prompt..."
                    />
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removePrompt(index)}
                      style={{ marginLeft: "8px", padding: "6px 10px" }}
                    >
                      <FontAwesomeIcon icon={faTrash} size="xs" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px 0",
            borderTop: "1px solid #f0f0f0",
            marginTop: "40px",
          }}
        >
          <div className="d-flex gap-3">
            <Button
              type="submit"
              style={{
                backgroundColor: "#F0D6B5",
                borderColor: "#F0D6B5",
                color: "#635956",
                fontWeight: "600",
                flex: 1,
                padding: "12px",
              }}
            >
              {isEdit ? "Update Lesson" : "Create Lesson"}
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={onCancel}
              style={{
                fontWeight: "600",
                padding: "12px",
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default LessonForm;
